import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Instagram, Twitter, Copy, Check, Image, Video, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from '../hooks/use-toast';

const promoVideos = [
  {
    id: 'video-horizontal',
    title: 'Promo Video — Landscape',
    platform: 'Twitter/X, YouTube, LinkedIn',
    dimensions: '1280x720',
    src: '/promo_video_horizontal.mp4',
    caption: `Build full-stack web & mobile apps in minutes with AI.

No coding required. Just describe what you want and watch it come to life.

SuperTool AI — The Future of App Building

Try free: [your-url]

#AI #AppDev #NoCode #BuildWithAI #TechStartup`,
  },
  {
    id: 'video-vertical',
    title: 'Promo Video — Vertical (Reels/TikTok)',
    platform: 'Instagram Reels, TikTok, YouTube Shorts',
    dimensions: '720x1280',
    src: '/promo_video_vertical.mp4',
    caption: `Build apps with AI in seconds.

No coding. Just vibes.

SuperTool AI turns your ideas into real apps.

Link in bio.

#AI #AppBuilder #NoCode #TechTok #SuperToolAI #BuildWithAI #Coding #Developer`,
  },
];

const promoAssets = [
  {
    id: 'ig-post-1',
    title: 'Instagram Post — Launch',
    platform: 'Instagram',
    dimensions: '1080x1080',
    image: '/promo_instagram_1.png',
    caption: `Build full-stack apps with AI in minutes. No coding required.

SuperTool AI turns your ideas into production-ready web & mobile apps using 6 AI models and 12 ready-made templates.

Try it free today.

#AI #AppDevelopment #NoCode #Startup #TechStartup #SuperToolAI #BuildWithAI #WebDev #MobileApp #SaaS`,
  },
  {
    id: 'ig-post-2',
    title: 'Instagram Post — Speed',
    platform: 'Instagram',
    dimensions: '1080x1080',
    image: '/promo_instagram_2.png',
    caption: `From idea to deployed app in 60 seconds.

Our AI agents handle frontend, backend, testing & deployment — all from a single chat interface.

50K+ builders already trust SuperTool AI.

Link in bio to start building.

#AI #ArtificialIntelligence #AppBuilder #Coding #Developer #TechTools #SuperToolAI #Innovation`,
  },
  {
    id: 'ig-story',
    title: 'Instagram/TikTok Story',
    platform: 'Instagram Story',
    dimensions: '1080x1920',
    image: '/promo_story.png',
    caption: `50K+ users trust SuperTool AI to build their apps.

6 AI Models. 12 Templates. Deploy instantly.

Swipe up to try free!`,
  },
  {
    id: 'twitter-banner',
    title: 'Twitter/X Banner or Post',
    platform: 'Twitter/X',
    dimensions: '1500x500',
    image: '/promo_twitter_banner.png',
    caption: `Build full-stack web & mobile apps in minutes with AI.

No coding. Just describe what you want.

SuperTool AI — The Future of App Building

Try free: [your-url]

#AI #AppDev #NoCode #BuildWithAI`,
  },
];

const PromoPage = () => {
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState(null);

  const copyCaption = (id, caption) => {
    navigator.clipboard.writeText(caption);
    setCopiedId(id);
    toast({ title: 'Caption copied!', description: 'Paste it into your social media post.' });
    setTimeout(() => setCopiedId(null), 2000);
  };

  const downloadImage = (src, title) => {
    const a = document.createElement('a');
    a.href = src;
    const ext = src.endsWith('.mp4') ? 'mp4' : 'png';
    a.download = `SuperToolAI_${title.replace(/\s+/g, '_')}.${ext}`;
    a.click();
    toast({ title: 'Downloading...', description: title });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-2 text-foreground/80 hover:text-foreground hover:bg-accent" data-testid="promo-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <Image className="w-7 h-7 text-pink-400" />
            <div>
              <h1 className="text-3xl font-bold text-foreground">Social Media Promo Kit</h1>
              <p className="text-muted-foreground mt-0.5">Download images and copy-paste captions for your social media posts</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Quick tips */}
        <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-6">
          <h3 className="text-foreground font-semibold mb-2">Posting Tips</h3>
          <ul className="text-muted-foreground text-sm space-y-1">
            <li>All platforms are <span className="text-emerald-400 font-medium">free</span> to post on (Instagram, Twitter/X, TikTok, LinkedIn)</li>
            <li>Post consistently (3-5x per week) to build visibility</li>
            <li>Use the square images (1080x1080) for Instagram feed posts</li>
            <li>Use the vertical image (1080x1920) for Instagram/TikTok Stories & Reels</li>
            <li>Use the wide image (1500x500) as your Twitter/X header or post image</li>
          </ul>
        </div>

        {/* Promo Videos */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Video className="w-5 h-5 text-purple-400" />
            <h2 className="text-xl font-bold text-foreground">Promo Videos</h2>
            <span className="text-xs px-2 py-0.5 bg-purple-500/10 text-purple-400 rounded-full border border-purple-500/20">AI Generated</span>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            {promoVideos.map((vid) => (
              <Card key={vid.id} className="bg-card border-border overflow-hidden" data-testid={`promo-card-${vid.id}`}>
                <CardContent className="p-0">
                  <div className="relative bg-background">
                    <video
                      src={vid.src}
                      controls
                      className="w-full aspect-video object-contain"
                      preload="metadata"
                      poster=""
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-base font-bold text-foreground">{vid.title}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs px-2 py-0.5 bg-purple-500/10 text-purple-400 rounded-full border border-purple-500/20">{vid.platform}</span>
                          <span className="text-xs text-muted-foreground">{vid.dimensions}</span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => downloadImage(vid.src, vid.title)}
                        className="bg-purple-600 hover:bg-purple-500 text-foreground"
                        data-testid={`download-${vid.id}`}
                      >
                        <Download className="w-4 h-4 mr-1" /> Download
                      </Button>
                    </div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Suggested Caption</label>
                    <div className="mt-1 bg-background border border-border rounded-xl p-3 max-h-[120px] overflow-y-auto">
                      <pre className="text-xs text-foreground/80 whitespace-pre-wrap font-sans leading-relaxed">{vid.caption}</pre>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyCaption(vid.id, vid.caption)}
                      className="mt-2 w-full border-input bg-muted text-foreground/80 hover:text-foreground hover:bg-accent"
                      data-testid={`copy-caption-${vid.id}`}
                    >
                      {copiedId === vid.id ? <><Check className="w-4 h-4 mr-2 text-emerald-400" /> Copied!</> : <><Copy className="w-4 h-4 mr-2" /> Copy Caption</>}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Promo Images */}
        <div className="flex items-center gap-2">
          <Image className="w-5 h-5 text-blue-400" />
          <h2 className="text-xl font-bold text-foreground">Promo Images</h2>
        </div>

        {promoAssets.map((asset) => (
          <Card key={asset.id} className="bg-card border-border overflow-hidden" data-testid={`promo-card-${asset.id}`}>
            <CardContent className="p-0">
              <div className="grid lg:grid-cols-2 gap-0">
                {/* Image */}
                <div className="relative bg-background flex items-center justify-center p-6">
                  <img
                    src={asset.image}
                    alt={asset.title}
                    className="max-w-full max-h-[400px] rounded-xl shadow-2xl object-contain"
                    loading="lazy"
                  />
                </div>

                {/* Details */}
                <div className="p-6 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-bold text-foreground">{asset.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs px-2 py-0.5 bg-blue-500/10 text-blue-400 rounded-full border border-blue-500/20">{asset.platform}</span>
                        <span className="text-xs text-muted-foreground">{asset.dimensions}</span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => downloadImage(asset.image, asset.title)}
                      className="bg-blue-600 hover:bg-blue-500 text-white"
                      data-testid={`download-${asset.id}`}
                    >
                      <Download className="w-4 h-4 mr-1" /> Download
                    </Button>
                  </div>

                  <div className="flex-1">
                    <label className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Suggested Caption</label>
                    <div className="mt-1 bg-background border border-border rounded-xl p-4 max-h-[220px] overflow-y-auto">
                      <pre className="text-sm text-foreground/80 whitespace-pre-wrap font-sans leading-relaxed">{asset.caption}</pre>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyCaption(asset.id, asset.caption)}
                    className="mt-3 w-full border-input bg-muted text-foreground/80 hover:text-foreground hover:bg-accent"
                    data-testid={`copy-caption-${asset.id}`}
                  >
                    {copiedId === asset.id ? <><Check className="w-4 h-4 mr-2 text-emerald-400" /> Copied!</> : <><Copy className="w-4 h-4 mr-2" /> Copy Caption</>}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Download All */}
        <div className="flex justify-center">
          <Button
            onClick={() => promoAssets.forEach((a) => downloadImage(a.image, a.title))}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white px-8 py-6 text-lg rounded-2xl"
            data-testid="download-all-btn"
          >
            <Download className="w-5 h-5 mr-2" /> Download All Assets
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PromoPage;
